import { gridSize } from '@/shared/constants';
import { Theme } from '@emotion/react';
import { FormControl, Grid, SxProps, TextField } from '@mui/material';

type CustomTextAreaNoFormProps = {
  label: string;
  helperText?: React.ReactNode;
  disabled?: boolean;
  readonly?:boolean;
  shrink?: boolean;
  required?: boolean;

  type?: React.HTMLInputTypeAttribute;

  size?: {
    xs: number;
    sm: number;
    md: number;
    lg: number;
  };

  defaultValue?: string | number;
  onChangeValue?: (value: any) => void;

  sizeTextField?: 'medium' | 'small';
  startAdornment?: React.ReactNode;

  sxTextField?: SxProps<Theme>;

  // text area
  rows?: number;
  minRows?: number;
  maxRows?: number;
  /** Si es true, el textarea crece con el contenido (minRows..maxRows) en vez de altura fija (rows). */
  autoGrow?: boolean;

  value: string | number;
};

const CustomTextAreaNoForm: React.FC<CustomTextAreaNoFormProps> = ({
  label,
  helperText,
  required = true,
  readonly = false,
  disabled = false,
  shrink = false,
  size = gridSize,
  type = 'text',

  defaultValue,
  onChangeValue,
  sizeTextField = 'medium',

  startAdornment,
  sxTextField,

  rows = 2,
  minRows = 1,
  maxRows = 12,
  autoGrow = false,
  value,
}) => {
  const isReadOnly = readonly && !disabled;

  const handleChange = (event: any) => {
    const currentValue = event.target.value;

    onChangeValue && onChangeValue(currentValue?.toUpperCase());
  };

  return (
    <Grid item {...size}>
      <FormControl fullWidth variant="outlined">
        <TextField
          size={sizeTextField}
          fullWidth
          variant="outlined"
          label={label}
          InputLabelProps={{
            ...(shrink && { shrink: true }),
            ...(startAdornment && { startAdornment }),
          }}
          helperText={helperText}
          type={type}
          defaultValue={defaultValue ?? ''}
          value={value}
          onChange={handleChange}
          required={required}
          disabled={disabled}
          inputProps={{
            readOnly: disabled || isReadOnly,
            tabIndex: disabled ? -1 : 0,
            style: disabled 
              ? { pointerEvents: 'none' } 
              : isReadOnly
                ? {userSelect:'text', cursor: 'text'}
                : undefined,
          }}
          sx={{
            ...(disabled
              ? {
                "& .MuiOutlinedInput-root": {
                  backgroundColor: "rgba(0, 0, 0, 0.035)",
                  cursor: "not-allowed",
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: "rgba(239, 239, 239, 0.16)",
                    borderStyle: "dashed",
                  },
                  "&:hover .MuiOutlinedInput-notchedOutline": {
                    borderColor: "rgba(0, 0, 0, 0.22)",
                  },
                },
                "& .MuiInputBase-input.Mui-disabled": {
                  WebkitTextFillColor: "rgba(15, 23, 42, 0.88)",
                  color: "rgba(15, 23, 42, 0.88)",
                  fontWeight: 600,
                  cursor: "not-allowed",
                },
                "& .MuiInputLabel-root.Mui-disabled": {
                  color: "text.secondary",
                  fontWeight: 500,
                },
              }
              : {
                "& .MuiOutlinedInput-root": {
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#d3d3d3",
                  },
                  "&:hover .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#9395ec",
                  },
                },
              }),
            ...sxTextField,
            ...(isReadOnly && {
              background: 'rgba(0,0,0,0.04)',
              borderRadius: 1
            })
          }}
          // text area
          multiline
          {...(autoGrow ? (disabled || isReadOnly ? { minRows } : { minRows, maxRows }) : { rows })}
        />
      </FormControl>
    </Grid>
  );
};

export default CustomTextAreaNoForm;
